import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { Permission, Tag } from '../api';
import { useAuth, useTenantSession } from '../auth';
import { Banner, Empty, Field, Modal, Spinner, TagEditor, TagList } from '../components/ui';

export function Permissions() {
  const { token } = useTenantSession();
  const { can } = useAuth();
  const [rows, setRows] = useState<Permission[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Permission | 'new' | null>(null);
  const [removing, setRemoving] = useState<Permission | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPermissions(token, search);
      setRows(res.permissions);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load permissions');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  async function onDelete() {
    if (!removing) return;
    try {
      await api.deletePermission(token, removing.id);
      setRemoving(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the permission');
      setRemoving(null);
    }
  }

  if (!can('permission.read')) {
    return <Empty>You do not have permission to view permissions.</Empty>;
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Permissions</h1>
          <p className="muted">Capability codes that roles grant to users in this application.</p>
        </div>
        {can('permission.create') && (
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            New permission
          </button>
        )}
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

      <input
        className="input input--search"
        placeholder="Search by name or code…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No permissions match.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Tags</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <code>{row.code}</code>
                  </td>
                  <td>{row.name}</td>
                  <td className="muted">{row.description || '—'}</td>
                  <td>
                    <TagList tags={row.tags} />
                  </td>
                  <td className="right row-actions">
                    {can('permission.update') && (
                      <button type="button" className="btn btn--small" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                    )}
                    {can('permission.delete') && (
                      <button type="button" className="btn btn--small btn--danger" onClick={() => setRemoving(row)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PermissionModal
          token={token}
          permission={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {removing && (
        <Modal
          title="Delete permission"
          onClose={() => setRemoving(null)}
          onSubmit={(e) => {
            e.preventDefault();
            void onDelete();
          }}
          submitLabel="Delete"
          destructive
        >
          <Banner kind="warn">
            Delete <code>{removing.code}</code>? It will also be removed from every role that grants it.
          </Banner>
        </Modal>
      )}
    </div>
  );
}

function PermissionModal({
  token,
  permission,
  onClose,
  onSaved,
}: {
  token: string;
  permission: Permission | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(permission?.name ?? '');
  const [code, setCode] = useState(permission?.code ?? '');
  const [description, setDescription] = useState(permission?.description ?? '');
  const [tags, setTags] = useState<Tag[]>(permission?.tags ?? []);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const body = {
      name: name.trim(),
      code: code.trim().toLowerCase(),
      description: description.trim(),
      tags: tags.filter((t) => t.key.trim()),
    };
    try {
      if (permission) await api.updatePermission(token, permission.id, body);
      else await api.createPermission(token, body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the permission');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={permission ? 'Edit permission' : 'New permission'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitting={submitting}
    >
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Name">
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Code" hint="Lowercase letters, digits and . _ : - as separators, e.g. report.read">
        <input className="input" required value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea
          className="input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Tags">
        <TagEditor value={tags} onChange={setTags} />
      </Field>
    </Modal>
  );
}
